import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import debounce from "lodash/debounce";
import { tv } from "tailwind-variants";
import { UseQueryResult } from "@tanstack/react-query";
import {
  PaginationState,
  useReactTable,
  getCoreRowModel,
  flexRender,
  SortingState,
  getSortedRowModel,
} from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faCircleNotch,
  faBarsSort,
  faArrowUpAZ,
  faArrowDownZA,
  faMagnifyingGlass,
  faTimes,
} from "@fortawesome/pro-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faExclamationTriangle } from "@fortawesome/pro-duotone-svg-icons";
import { Button } from "@/components/Button";
import { H4 } from "@/components/Typography";
import { Trans, useTranslation } from "react-i18next";

const styles = tv({
  slots: {
    parent: "flex w-full flex-col gap-20",
    inputContainer: "relative w-full sm:w-1/3",
    input:
      "w-full rounded border border-transparent bg-gray-light px-20 py-10 placeholder:text-gray-dark focus:border-gray focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
    inputIconContainer: "absolute right-0 top-0 px-20 py-10 text-gray-dark",
    clearIcon: "hover:text-secondary",
    placeholder: "flex h-[200px] w-full flex-col items-center justify-center",
    placeholderLoading: "flex flex-row items-center gap-10",
    placeholderErrorContainer: "flex flex-col items-center gap-10",
    placeholderErrorHeading: "font-bold",
    placeholderErrorIcon: "mr-10",
    placeholderErrorText: "flex flex-row items-center gap-10",
    tableWrapper: "-mx-20 max-w-[100vw] overflow-x-auto sm:mx-0",
    table: "w-full",
    thead: "border-b border-gray-dark",
    th: "px-20 py-10 text-left",
    thSort: "group flex items-center text-small font-semibold uppercase",
    tbody: "",
    tr: "border-y border-gray even:bg-gray-light/50",
    td: "px-20 py-10",
    sortButton:
      "ml-10 inline-flex cursor-pointer rounded bg-gray-light p-5 transition-colors group-hover:bg-secondary group-hover:text-white",
    detailsRow:
      "flex flex-col items-center justify-center gap-20 sm:flex-row sm:justify-between sm:gap-10",
    details: "flex flex-row items-center gap-5",
    buttons:
      "flex w-full flex-row justify-between gap-10 sm:ml-auto sm:w-[unset] sm:justify-end",
    button:
      "rounded bg-gray-light px-10 py-10 transition-colors enabled:hover:bg-secondary enabled:hover:text-white disabled:cursor-not-allowed disabled:opacity-50",
  },
  variants: {
    isTableFetching: {
      true: {
        tbody: "opacity-50",
      },
    },
    sortable: {
      true: {
        thSort: "cursor-pointer select-none",
      },
    },
    align: {
      left: {
        thSort: "justify-start text-left",
        td: "text-left",
      },
      center: { thSort: "justify-center text-center", td: "text-center" },
      right: { thSort: "justify-end text-right", td: "text-right" },
    },
  },
});

interface IFilters {
  [key: string]: string | boolean | number;
}

interface IDefaultFilters {
  pageSize?: number;
  page?: number;
}

interface IProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: any;
  queryKey: string[];
  queryHook: (options: ITableOptions) => UseQueryResult<ITableRS>;
  filters?: IFilters;
  isSearchEnabled?: boolean;
  isPageInformationEnabled?: boolean;
  isSortEnabled?: boolean;
  denyList?: string[];
  defaultFilters?: IDefaultFilters;
}

const Table = ({
  columns,
  queryKey,
  queryHook,
  filters,
  isSearchEnabled = true,
  isPageInformationEnabled = true,
  isSortEnabled = false,
  denyList,
  defaultFilters,
}: IProps) => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams({
    page: defaultFilters?.page?.toString() || "1",
    pageSize: defaultFilters?.pageSize?.toString() || "25",
  });

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: Number(searchParams.get("page")) - 1,
    pageSize: Number(searchParams.get("pageSize")),
  });

  const [sorting, setSorting] = useState<SortingState>([]);

  const [searchString, setSearchString] = useState<string>(
    searchParams.get("searchString") || "",
  );
  const [debouncedSearchString, setDebouncedSearchString] = useState<string>(
    searchParams.get("searchString") || searchString,
  );

  useEffect(() => {
    // If there is a redirect it means we are login out. Don't overwrite current params
    if (!location.search.includes("redirect")) {
      handleParamsChange();
    }
  }, [searchParams, pageIndex, pageSize, sorting, debouncedSearchString]);

  const shouldRunQuery = (): boolean => {
    if (!filters) return !!searchParams?.toString();

    /**
     * This ensures that all filters have been placed in the URL before
     * the query can run. i.e. on the first render the query shouldn't run
     * until the filters have been placed in the searchParams.
     *
     * This ensures no duplicate requests are made.
     */
    const filterKeys = Object.keys(filters);
    const searchParamKeys = Array.from(searchParams.keys());
    const keys = [...searchParamKeys, ...(denyList || [])];

    return filterKeys.every(key => keys.includes(key));
  };

  const dataQuery = queryHook({
    enabled: shouldRunQuery(),
    queryKey: queryKey.concat(searchParams.toString()),
    params: {
      ...Object.fromEntries([...searchParams]),
      ...filters,
    },
  });

  const handleMultiSorting = () => {
    const sortBy: string[] = [];
    const sortDirection: string[] = [];

    sorting.map(item => {
      sortBy.push(item.id);
      sortDirection.push(item.desc ? "DESC" : "ASC");
    });

    if (!sortBy.length && !sortDirection.length) return null;

    return {
      sortBy: sortBy.join(","),
      sortDirection: sortDirection.join(","),
    };
  };

  const createOrUpdateSearchParam = ({
    prevParams,
    key,
    value,
  }: {
    prevParams: URLSearchParams;
    key: string;
    value: string;
  }) => {
    const existingValue = prevParams.get(key);

    if (!existingValue) {
      prevParams.set(key, value);
    } else if (!!existingValue && existingValue !== value) {
      prevParams.set(key, value);
    }
  };

  const handleParamsChange = () => {
    setSearchParams(
      prevParams => {
        /**
         * Loop the filters being passed in, if they do not exist,
         * then we need to add them to the query string. If they do
         * exist, then we need to:
         * 1. Do nothing if the values are the same
         * 2. Update the value if the filter != current search param
         */
        if (filters) {
          for (const [key, value] of Object.entries(filters)) {
            if (denyList?.includes(key)) continue;

            createOrUpdateSearchParam({
              prevParams,
              key,
              value: value?.toString(),
            });
          }
        }

        /**
         * Set the page and pageSize values
         */
        createOrUpdateSearchParam({
          prevParams,
          key: "page",
          value: (pageIndex + 1).toString(),
        });

        createOrUpdateSearchParam({
          prevParams,
          key: "pageSize",
          value: pageSize.toString(),
        });

        const sortModel = handleMultiSorting();

        if (sortModel) {
          createOrUpdateSearchParam({
            prevParams,
            key: "sortBy",
            value: sortModel.sortBy,
          });
          createOrUpdateSearchParam({
            prevParams,
            key: "sortDirection",
            value: sortModel.sortDirection,
          });
        } else {
          prevParams.delete("sortBy");
          prevParams.delete("sortDirection");
        }

        if (debouncedSearchString) {
          createOrUpdateSearchParam({
            prevParams,
            key: "searchString",
            value: debouncedSearchString,
          });
        } else {
          prevParams.delete("searchString");
        }

        return prevParams;
      },
      {
        replace: true,
      },
    );
  };

  const handleSearchStringChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
    handleDebouncedSearchStringChange(e);
  };

  const handleClearSearchString = () => {
    setSearchString("");
    setDebouncedSearchString("");
  };

  const handleDebouncedSearchStringChange = useMemo(
    () =>
      debounce((e: ChangeEvent<HTMLInputElement>) => {
        setDebouncedSearchString(e.target.value);
      }, 1000),
    [],
  );

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  const {
    getRowModel,
    getState,
    getPageCount,
    getCanPreviousPage,
    previousPage,
    nextPage,
    getHeaderGroups,
  } = useReactTable({
    data: dataQuery.data?.results ?? [],
    columns,
    pageCount: dataQuery.data?.pages ?? -1,
    state: {
      pagination,
      sorting,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  const {
    isLoading: isTableLoading,
    isFetching: isTableFetching,
    isError: isTableError,
    data: tableData,
  } = dataQuery;

  const {
    parent,
    inputContainer,
    input,
    inputIconContainer,
    clearIcon,
    placeholder,
    placeholderLoading,
    placeholderErrorContainer,
    placeholderErrorHeading,
    placeholderErrorIcon,
    placeholderErrorText,
    tableWrapper,
    table: tableContainer,
    thead,
    th,
    thSort,
    tbody,
    tr,
    td,
    sortButton,
    detailsRow,
    details,
    buttons,
    button,
  } = styles({ isTableFetching });

  const renderSearchInputIcon = () =>
    !!debouncedSearchString ? (
      <button className={clearIcon()} onClick={handleClearSearchString}>
        <FontAwesomeIcon
          fixedWidth
          icon={isTableFetching ? faCircleNotch : faTimes}
          spin={isTableFetching}
        />
      </button>
    ) : (
      <FontAwesomeIcon
        fixedWidth
        icon={isTableFetching ? faCircleNotch : faMagnifyingGlass}
        spin={isTableFetching}
      />
    );

  const renderSearchInput = () => (
    <div className={inputContainer()}>
      <input
        type="text"
        value={searchString}
        onChange={handleSearchStringChange}
        className={input()}
        disabled={isTableError}
        placeholder={t("components.table.searchPlaceholder")}
        data-testid="table-search"
      />
      <span className={inputIconContainer()}>{renderSearchInputIcon()}</span>
    </div>
  );

  const renderTableHead = () => (
    <thead className={thead()} data-testid="table-head">
      {getHeaderGroups().map(({ id: headerGroupId, headers }) => (
        <tr key={headerGroupId}>
          {headers.map(
            ({ id: headerId, colSpan, isPlaceholder, column, getContext }) => (
              <th
                key={headerId}
                colSpan={colSpan}
                className={th()}
                role="cell"
                style={{
                  width: column.getSize(),
                }}>
                {!isPlaceholder && (
                  <div
                    className={thSort({
                      sortable: column.getCanSort() && isSortEnabled,
                      align: (column.columnDef?.meta as ITableMeta)?.align,
                    })}
                    onClick={
                      isSortEnabled && column.getCanSort()
                        ? column.getToggleSortingHandler()
                        : undefined
                    }>
                    {flexRender(column.columnDef.header, getContext())}
                    {(column.getCanSort() &&
                      isSortEnabled &&
                      {
                        asc: renderSorting("asc"),
                        desc: renderSorting("desc"),
                      }[column.getIsSorted() as string]) ??
                      renderSorting("none")}
                  </div>
                )}
              </th>
            ),
          )}
        </tr>
      ))}
    </thead>
  );

  const renderSorting = (direction: "asc" | "desc" | "none") => (
    <button className={sortButton()}>
      <FontAwesomeIcon
        fixedWidth
        icon={
          direction === "none"
            ? faBarsSort
            : direction === "asc"
              ? faArrowUpAZ
              : faArrowDownZA
        }
      />
    </button>
  );

  const renderLoadingState = () => (
    <span className={placeholderLoading()}>
      <FontAwesomeIcon fixedWidth icon={faCircleNotch} spin />
      {t("generic.loading")}
    </span>
  );

  const renderErrorState = () => (
    <div className={placeholderErrorContainer()}>
      <H4 className={placeholderErrorHeading()}>
        <FontAwesomeIcon
          fixedWidth
          icon={faExclamationTriangle}
          className={placeholderErrorIcon()}
        />
        {t("components.table.error.heading")}
      </H4>
      <span className={placeholderErrorText()}>
        <Trans i18nKey="components.table.error.description">
          zero
          <Button as="link" variant="link-light" to="/support" width="unset">
            one
          </Button>
        </Trans>
      </span>
    </div>
  );

  const renderPlaceholderBody = () => (
    <div className={placeholder()} data-testid="table-placeholder-container">
      {isTableFetching && !isTableError && renderLoadingState()}
      {!isTableFetching && isTableError && renderErrorState()}
    </div>
  );

  const renderTableBody = () => (
    <tbody className={tbody()} data-testid="table-body">
      {getRowModel().rows.length > 0 ? (
        getRowModel().rows.map(row => (
          <tr key={row.id} className={tr()} data-testid="table-row">
            {row.getVisibleCells().map(cell => {
              return (
                <td
                  key={cell.id}
                  className={td({
                    align: (cell.column.columnDef?.meta as ITableMeta)?.align,
                  })}
                  role="cell">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              );
            })}
          </tr>
        ))
      ) : !isTableFetching && tableData?.results?.length === 0 ? (
        <tr data-testid="table-empty-container">
          <td className="h-[200px] text-center" colSpan={columns.length}>
            {t("components.table.empty")}
          </td>
        </tr>
      ) : null}
    </tbody>
  );

  const renderButton = ({
    icon,
    action,
    disabled,
    testId,
  }: {
    icon: IconProp;
    action: () => void;
    disabled: boolean;
    testId: string;
  }) => (
    <button
      className={button()}
      onClick={action}
      disabled={disabled || isTableFetching || isTableError}
      data-testid={testId}>
      <FontAwesomeIcon fixedWidth icon={icon} />
    </button>
  );

  const renderPageInformation = () => (
    <span data-testid="table-page-information">
      <Trans
        i18nKey="components.table.totalInfo"
        values={{
          pageSize,
        }}
      />
    </span>
  );

  const renderDetailsAndButtons = (position: "top" | "bottom") => (
    <div className={detailsRow()}>
      {position === "top" && isSearchEnabled && renderSearchInput()}
      {position === "bottom" &&
        !isTableLoading &&
        isPageInformationEnabled &&
        renderPageInformation()}

      <div className={buttons()}>
        {tableData && !isTableError && (
          <div className={details()}>
            <Trans
              i18nKey="components.table.pageInfo"
              values={{
                pageIndex: getState().pagination.pageIndex + 1,
                pageCount: getPageCount() >= 1 ? getPageCount() : "1",
              }}>
              zero
              <strong>one</strong>
              two
            </Trans>
          </div>
        )}

        {renderButton({
          icon: faChevronLeft,
          action: () => previousPage(),
          disabled: !getCanPreviousPage(),
          testId: "table-button-previous-page",
        })}
        {renderButton({
          icon: faChevronRight,
          action: () => nextPage(),
          disabled: !!tableData?.results?.length
            ? tableData?.results?.length < pageSize
            : true,
          testId: "table-button-next-page",
        })}
      </div>
    </div>
  );

  return (
    <div className={parent()}>
      {renderDetailsAndButtons("top")}
      <div className={tableWrapper()}>
        <table role="table" className={tableContainer()}>
          {renderTableHead()}
          {(!isTableError || (isTableFetching && tableData)) &&
            renderTableBody()}
        </table>
      </div>
      {(isTableError || (isTableFetching && !tableData)) &&
        renderPlaceholderBody()}
      {renderDetailsAndButtons("bottom")}
    </div>
  );
};

export default Table;
